import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { getDrafts, deleteDraft } from "../../api/orders";
import type { Draft } from "../../types/order.types";
import { DraftCard } from "./DraftCard";

interface DraftsPanelProps {
  initialDrafts: Draft[];
  initialTotalPages: number;
  onApplyDraft: (draft: Draft) => void;
}

export function DraftsPanel({
  initialDrafts,
  initialTotalPages,
  onApplyDraft,
}: DraftsPanelProps) {
  const [drafts, setDrafts] = useState<Draft[]>(initialDrafts);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Draft | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadMoreRef = useRef<() => void>(() => {});

  const loadMore = () => {
    if (isLoadingMore || currentPage >= totalPages) return;
    setIsLoadingMore(true);
    getDrafts(currentPage + 1, 10)
      .then((res) => {
        setDrafts((prev) => [...prev, ...res.data]);
        setCurrentPage(res.page);
        setTotalPages(res.totalPages);
      })
      .finally(() => setIsLoadingMore(false));
  };

  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!scrollContainer || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root: scrollContainer, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteDraft(deleteTarget.id);
      setDrafts((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      toast.error("Не удалось удалить черновик");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b shrink-0">
        <h3 className="font-semibold text-sm">Черновики</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Выберите черновик, чтобы быстро заполнить форму
        </p>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {drafts.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет сохранённых черновиков
          </p>
        )}
        {drafts.map((draft) => (
          <DraftCard
            key={draft.id}
            draft={draft}
            onUse={() => onApplyDraft(draft)}
            onDelete={() => setDeleteTarget(draft)}
          />
        ))}
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Удалить черновик?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Это действие нельзя отменить.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Отмена
            </Button>
            <Button variant="destructive" disabled={isDeleting} onClick={handleDeleteConfirm}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
